import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'common/bento/types/Abstract';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';

import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  ACTIVITY_OUTSIDE_EU = 'activity-outside-eu',
  SAVE_ACTIVITY_OUTSIDE_EU = 'save-activity-outside-eu',
  COMPANY_MEMBER_SANCTIONS = 'company-member-sanctions',
  SAVE_COMPANY_MEMBER_SANCTIONS = 'save-company-member-sanctions',
  COMPANY_SANCTIONS = 'company-sanctions',
  SAVE_COMPANY_SANCTIONS = 'save-company-sanctions',
  INELIGIBLE = 'ineligible',
  ABORT = 'abort',
  DONE = 'done',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'company-compliance',

    initial: State.ACTIVITY_OUTSIDE_EU,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.ACTIVITY_OUTSIDE_EU]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.ABORT,
          SUBMIT_ACTIVITY_OUTSIDE_EU: [
            // We're not saving hasActivityOutsideEU if it's true as POST /provide-company-sanctions-declaration
            // expects the other sanctions fields to be sent in that case
            {
              cond: Guard.HasActivityOutsideEU,
              target: State.COMPANY_MEMBER_SANCTIONS,
            },
            {
              target: State.SAVE_ACTIVITY_OUTSIDE_EU,
            },
          ],
        },
      },

      [State.SAVE_ACTIVITY_OUTSIDE_EU]: {
        invoke: {
          onDone: State.DONE,
          onError: {
            actions: shared.actions.error(),
            target: State.ACTIVITY_OUTSIDE_EU,
          },
          src: Service.SaveActivityOutsideEu,
        },
      },

      [State.COMPANY_MEMBER_SANCTIONS]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.ACTIVITY_OUTSIDE_EU,
          SUBMIT_COMPANY_MEMBER_SANCTIONS: State.SAVE_COMPANY_MEMBER_SANCTIONS,
        },
      },

      [State.SAVE_COMPANY_MEMBER_SANCTIONS]: {
        invoke: {
          onDone: [
            {
              cond: Guard.HasSanctionedMember,
              target: State.INELIGIBLE,
            },
            {
              target: State.COMPANY_SANCTIONS,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.COMPANY_MEMBER_SANCTIONS,
          },
          src: Service.SaveCompanyCompliance,
        },
      },

      [State.COMPANY_SANCTIONS]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.COMPANY_MEMBER_SANCTIONS,
          SUBMIT_COMPANY_SANCTIONS: State.SAVE_COMPANY_SANCTIONS,
        },
      },

      [State.SAVE_COMPANY_SANCTIONS]: {
        invoke: {
          onDone: [
            {
              cond: Guard.HasSanctions,
              target: State.INELIGIBLE,
            },
            {
              target: State.DONE,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.COMPANY_SANCTIONS,
          },
          src: Service.SaveCompanyCompliance,
        },
      },

      [State.INELIGIBLE]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.COMPANY_MEMBER_SANCTIONS,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },
  {
    actions,
    guards,
    services,
  },
);

export default machine;
